import { Flex, IconButton, Popover, Portal, Text } from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';

import { ChangeEvent, Menu, MenuContent, MenuItem } from '@components/common/Menu';
import useControlledPopover from 'hooks/useControlledPopover';
import { useMarketPlaceSortOptions } from 'hooks/useMarketPlaceSortOptions';
import { isNoneOption } from 'stores/marketPlaceStore';
import { useStores } from 'stores/rootStore';
import RawArrowIcon from 'vectors/Arrowhead';

const SortByControls = () => {
  const { open, onToggle, triggerRef, contentRef } = useControlledPopover<
    HTMLDivElement,
    HTMLButtonElement
  >();

  const { selectedOption } = useMarketPlaceSortOptions();

  return (
    <Popover.Root open={open} onOpenChange={onToggle} closeOnEscape unmountOnExit lazyMount>
      <Popover.Trigger asChild>
        <IconButton
          _hover={{
            backgroundColor: 'button.secondary.hover',
          }}
          ref={triggerRef}
          variant="ghost"
          px={2}
        >
          <Flex alignItems="center" flexDir="row" gap={2}>
            <Text>Sort by {isNoneOption(selectedOption?.id) ? '' : ':'}</Text>
            {!isNoneOption(selectedOption?.id) && (
              <Text color="text.secondary">{selectedOption?.label}</Text>
            )}
            <RawArrowIcon boxSize="8px" direction="down" />
          </Flex>
        </IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content borderRadius={8} ref={contentRef} bgColor="surface.background" p={2}>
            <SortMenu />
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};

const SortMenu = observer(() => {
  const { marketPlaceStore } = useStores();
  const { sortOptions, selectedOption } = useMarketPlaceSortOptions();

  const onSelect = useCallback(
    (e: ChangeEvent) => {
      marketPlaceStore.setSortBy(e.value);
    },
    [marketPlaceStore],
  );

  return (
    <Menu showSelected value={selectedOption?.id} onChange={onSelect} focus fontSize="xs">
      {sortOptions.map((option) => (
        <MenuItem
          key={option.id}
          value={option.id}
          pr="2px"
          justifyContent="space-between"
          alignItems="center"
          textOverflow="ellipsis"
        >
          <MenuContent
            value={
              <Text fontWeight="medium" fontSize="sm">
                {option.label}
              </Text>
            }
          />
        </MenuItem>
      ))}
    </Menu>
  );
});

export default observer(SortByControls);
